/* eslint-disable @typescript-eslint/no-explicit-any */
import { useEffect, useState } from 'react'
import { Checkbox, Radio, RadioGroup, FormControlLabel } from '@mui/material'
import Counter from '../Counter'
import {
  ProductOptionsContainer,
  ProductOptionsRow,
  ProductsOptionsHeader,
  Label
} from './ProductOptions.styles'
import { IProductOptions, ItemListType } from './ProductOptions.types'
import { IActionsParams, IActions } from '../../../interfaces/actions'
import { getLabelForOption } from './ProductOptions.utils'

const ProductOptions = ({
  bundleTexts: {
    label = '',
    descriptionForCounters = '',
    descriptionForCheckBox = '',
    descriptionForRadio = '',
    descriptionForRangeAmount = '',
    multiplierMark = 'x'
  },
  bundle,
  bundlesExtra,
  bundlesHandler,
  subItem
}: IProductOptions) => {
  const {
    items = [],
    minQtyPerSku,
    maxQtyPerSku,
    minSkus,
    maxSkus,
    totalItems,
    minTotalQty,
    maxTotalQty
  } = bundle
  type TSelected = string[]
  // for Checkbox
  const [selected, setSelected] = useState<TSelected>([])
  // for RadioGroup
  const [selectedRadio, setSelectedRadio] = useState<string>('')
  // for counters
  const [counterList, setCounterList] = useState<ItemListType>([])
  const [counterListSku, setCounterListSku] = useState<string[]>([])
  const [totalItemsSelected, setTotalItemsSelected] = useState<number>(0)
  const list = bundlesExtra.filter(({ sku = '' }) => items?.includes(sku))

  useEffect(() => {
    if (subItem) {
      const skuList = Object.keys(subItem.items)
      if (maxSkus > 1 && minQtyPerSku === maxQtyPerSku) {
        const count = skuList.length * minQtyPerSku
        setSelected(skuList)
        setTotalItemsSelected(count)
      } else {
        const listSkuCounter = skuList.map((sku) => ({
          [sku]: subItem.items[sku]
        }))
        setCounterList(listSkuCounter)
        setCounterListSku(skuList)
        const count =
          listSkuCounter.length > 0
            ? listSkuCounter.reduce(
                (acc, item) => acc + Object.values(item)[0],
                0
              )
            : 0
        setTotalItemsSelected(count)
      }
    }
  }, [subItem])

  const validateCompleteCheckbox = (listCount: number) => {
    return listCount === totalItems
  }

  const validateCompleteCounter = (
    listCount: number,
    listItems: ItemListType,
    skuList: string[]
  ) => {
    // minValidSkus is the list of items that have a quantity greater than or equal to minQtyPerSku
    const minQtyValidSkus = listItems.filter(
      (item) => Object.values(item)[0] >= minQtyPerSku
    )
    if (totalItems) {
      return (
        totalItems === listCount && minQtyValidSkus.length === skuList.length
      )
    }
    if (minTotalQty && maxTotalQty) {
      return (
        listCount >= minTotalQty &&
        listCount <= maxTotalQty &&
        minQtyValidSkus.length >= skuList.length &&
        minSkus <= skuList.length
      )
    }
    return false
  }
  // Provisional approach
  // this approach is not correct because the event is not the same as the one in the correct approach
  // but something is running wrong with onChange events for MUI components
  const handleClickRadio = (e: React.MouseEvent<HTMLInputElement>) => {
    const { name } = e.target as HTMLInputElement
    setSelectedRadio(name)
    bundlesHandler({
      selections: [{ [name]: minQtyPerSku }],
      completed: true,
      item: bundle
    })
  }

  const handleClickCheckBox = (e: React.MouseEvent<HTMLInputElement>) => {
    const { name } = e.target as HTMLInputElement
    const checked = selected.includes(name)
    let newSelectedList = [...selected]
    if (checked) {
      newSelectedList = selected.filter((item) => item !== name)
      setSelected(newSelectedList)
    } else {
      if (selected.length === maxSkus) {
        return
      }
      newSelectedList = [...selected, name]
      setSelected(newSelectedList)
    }
    const count = newSelectedList.length * minQtyPerSku
    setTotalItemsSelected(count)
    const isComplete = validateCompleteCheckbox(count)
    const formatedList = newSelectedList.map((item) => ({
      [item]: minQtyPerSku
    }))
    bundlesHandler({
      selections: formatedList,
      completed: isComplete,
      item: bundle
    })
  }

  const handlePlusCounterAction = (params: IActionsParams) => {
    const {
      newValue,
      data: { name }
    } = params
    let newCounterList = []
    const newCounterListSku = [...counterListSku]
    if (newValue === 1) {
      newCounterListSku.push(name)
      setCounterListSku(newCounterListSku)
      newCounterList = [...counterList, { [name]: newValue }]
      setCounterList(newCounterList)
    } else {
      const current = counterList.findIndex(
        (item) => Object.keys(item)[0] === name
      )
      newCounterList = [...counterList]
      newCounterList[current] = { [name]: newValue }
      setCounterList(newCounterList)
    }
    const count =
      newCounterList.length > 0
        ? newCounterList.reduce((acc, item) => acc + Object.values(item)[0], 0)
        : 0
    setTotalItemsSelected(count)
    const isComplete = validateCompleteCounter(
      count,
      newCounterList,
      newCounterListSku
    )
    bundlesHandler({
      selections: newCounterList,
      completed: isComplete,
      item: bundle
    })
  }

  const handleMinusCounterAction = (params: IActionsParams) => {
    const {
      newValue,
      data: { name }
    } = params
    let newCounterList = []
    const newCounterListSku = [...counterListSku]
    if (newValue === 0) {
      const removeIndex = newCounterListSku.indexOf(name)
      newCounterListSku.splice(removeIndex, 1)
      setCounterListSku(newCounterListSku)
      newCounterList = counterList.filter(
        (item) => Object.keys(item)[0] !== name
      )
      setCounterList(newCounterList)
    } else {
      const current = counterList.findIndex(
        (item) => Object.keys(item)[0] === name
      )
      newCounterList = [...counterList]
      newCounterList[current] = { [name]: newValue }
      setCounterList(newCounterList)
    }
    const count =
      newCounterList.length > 0
        ? newCounterList.reduce((acc, item) => acc + Object.values(item)[0], 0)
        : 0
    setTotalItemsSelected(count)
    const isComplete = validateCompleteCounter(
      count,
      newCounterList,
      newCounterListSku
    )
    bundlesHandler({
      selections: newCounterList,
      completed: isComplete,
      item: bundle
    })
  }

  const renderOptions = () => {
    if (maxSkus === 1) {
      return (
        <RadioGroup row value={selectedRadio}>
          {list.map(({ name, sku, suffixLabel }) => {
            return (
              <ProductOptionsRow key={`radio-product-options-${sku}`}>
                <FormControlLabel
                  key={sku}
                  labelPlacement="start"
                  label={
                    <Label>
                      {getLabelForOption({
                        name,
                        useCounter: false,
                        multiplierMark,
                        minQtyPerSku,
                        suffixLabel
                      })}
                    </Label>
                  }
                  value={sku}
                  control={
                    <Radio onClick={handleClickRadio as any} name={sku} />
                  }
                />
              </ProductOptionsRow>
            )
          })}
        </RadioGroup>
      )
    }
    if (maxSkus > 1 && minQtyPerSku === maxQtyPerSku) {
      return list.map(({ name, sku = '', suffixLabel }) => {
        const disabled =
          totalItems === totalItemsSelected && !selected.includes(sku)
        return (
          <ProductOptionsRow key={sku}>
            <Label>
              {getLabelForOption({
                name,
                useCounter: false,
                multiplierMark,
                minQtyPerSku,
                suffixLabel
              })}
            </Label>
            <Checkbox
              onClick={handleClickCheckBox as any}
              name={sku}
              checked={selected.includes(sku)}
              disabled={disabled}
            />
          </ProductOptionsRow>
        )
      })
    }
    return list.map(({ name, sku = '', suffixLabel }) => {
      let minForDisabled = 0
      if (totalItems) {
        minForDisabled = totalItems
      }
      if (minTotalQty && maxTotalQty) {
        minForDisabled = maxTotalQty
      }
      const maxValues = totalItems || maxTotalQty
      const isDisabled =
        (!counterListSku.includes(sku) && counterListSku.length === maxSkus) ||
        maxValues === totalItemsSelected ||
        (!counterListSku.includes(sku) &&
          minForDisabled - totalItemsSelected < minQtyPerSku)
      const currentElement = counterList.find((item) => item[sku])
      return (
        <ProductOptionsRow key={sku}>
          <Label>
            {getLabelForOption({
              name,
              useCounter: true,
              suffixLabel
            })}
          </Label>
          <Counter
            data={{ name: sku }}
            value={currentElement?.[sku] || 0}
            plusButton={{
              actions: handlePlusCounterAction as IActions,
              disabled: isDisabled
            }}
            minusButton={{ actions: handleMinusCounterAction as IActions }}
            max={maxQtyPerSku}
          />
        </ProductOptionsRow>
      )
    })
  }

  const renderDescription = () => {
    let description = ''
    if (totalItems) {
      description = descriptionForCounters.replace(
        '{totalItems}',
        `${totalItems}`
      )
    }
    if (minTotalQty && maxTotalQty) {
      description = descriptionForRangeAmount.replace(
        '{minTotalQty}',
        `${minTotalQty}`
      )
      description = description.replace('{maxTotalQty}', `${maxTotalQty}`)
    }
    if (maxSkus === 1) {
      description = descriptionForRadio
    } else if (maxSkus > 1 && minQtyPerSku === maxQtyPerSku) {
      description = descriptionForCheckBox.replace(
        '{totalOptions}',
        `${minSkus}`
      )
    }
    return <span className="header-texts">{description}</span>
  }

  return (
    <ProductOptionsContainer>
      <ProductsOptionsHeader>
        {label && <span className="header-texts">{label}</span>}
        {renderDescription()}
      </ProductsOptionsHeader>
      {renderOptions()}
    </ProductOptionsContainer>
  )
}

export default ProductOptions
